import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Field, Stat, formatMoney } from '@promtver/ui-kit';

import { getPartnerReport } from '../../api/partner';
import { AppShell } from '../../components/AppShell';
import { AsyncState } from '../../components/AsyncState';
import { KeyValue } from '../../components/KeyValue';
import { ru } from '../../i18n/ru';
import { downloadCsv, type CsvCell } from '../../lib/csv';
import { addDaysInputValue, formatDateTimeLocal, formatPercent, todayInputValue } from '../../lib/datetime';

/**
 * Отчётность предприятия (FR-MGR-04): посещаемость по периодам, доля повторных
 * гостей, возрастная структура, загрузка слотов, выручка по программам,
 * выгрузка в CSV (UTF-8 с BOM).
 */
export function ReportsPage() {
  const [from, setFrom] = useState(addDaysInputValue(-30));
  const [to, setTo] = useState(todayInputValue());

  const reportQuery = useQuery({
    queryKey: ['partner', 'report', from, to],
    queryFn: () => getPartnerReport(from, to),
  });

  const report = reportQuery.data;

  const slotsLoad = report?.slotsLoad ?? [];
  const averageLoad = (() => {
    if (slotsLoad.length === 0) return 0;
    const sum = slotsLoad.reduce((acc, item) => acc + (item.capacity > 0 ? item.reserved / item.capacity : 0), 0);
    return sum / slotsLoad.length;
  })();

  const exportCsv = () => {
    if (!report) return;
    const rows: CsvCell[][] = [];
    rows.push([ru.reports.title]);
    rows.push([ru.reports.period, from, to]);
    rows.push([]);
    rows.push([ru.reports.visits, report.visits]);
    rows.push([ru.reports.repeatRate, formatPercent(report.repeatRate)]);
    rows.push([ru.reports.revenue, report.revenue]);
    rows.push([ru.reports.slotsLoad, formatPercent(averageLoad)]);
    rows.push([]);
    rows.push([ru.reports.ageStructure, ru.common.status]);
    for (const bucket of report.ageStructure ?? []) {
      rows.push([bucket.bucket, bucket.count]);
    }
    rows.push([]);
    rows.push([ru.reports.revenueByProgram, ru.reports.visits, ru.reports.revenue]);
    for (const item of report.revenueByProgram ?? []) {
      rows.push([item.title, item.visits, (item.revenue / 100).toFixed(2)]);
    }
    rows.push([]);
    rows.push([ru.reports.slotsLoad, ru.schedule.reserved, ru.schedule.capacity]);
    for (const slot of slotsLoad) {
      rows.push([formatDateTimeLocal(slot.startsAt), slot.reserved, slot.capacity]);
    }
    rows.push([]);
    rows.push([ru.reports.attendance, ru.reports.visits, ru.common.status]);
    for (const item of report.attendanceByPeriod ?? []) {
      rows.push([item.period, item.visits, item.bookings]);
    }

    downloadCsv(`promtver-otchet-${from}_${to}.csv`, rows);
  };

  return (
    <AppShell title={ru.reports.title} subtitle={ru.reports.subtitle} active="reports">
      <Card title={ru.reports.period}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={ru.common.from} value={from} onChange={setFrom} type="date" required />
          <Field label={ru.common.to} value={to} onChange={setTo} type="date" required />
        </div>
      </Card>

      <AsyncState
        isLoading={reportQuery.isLoading}
        isError={reportQuery.isError}
        onRetry={() => void reportQuery.refetch()}
      >
        {report ? (
          <>
            <Card title={ru.reports.title}>
              <div className="grid grid-cols-2 gap-4">
                <Stat label={ru.reports.visits} value={report.visits} />
                <Stat label={ru.reports.repeatRate} value={formatPercent(report.repeatRate)} />
                <Stat label={ru.reports.revenue} value={formatMoney(report.revenue)} />
                <Stat label={ru.reports.slotsLoad} value={formatPercent(averageLoad)} />
              </div>
              <Button tone="accent" onClick={exportCsv} disabled={!report}>
                {ru.reports.exportCsv}
              </Button>
            </Card>

            <Card title={ru.reports.ageStructure}>
              {report.ageStructure && report.ageStructure.length > 0 ? (
                <ul className="flex flex-col gap-2">
                  {report.ageStructure.map((bucket) => {
                    const max = Math.max(
                      ...report.ageStructure.map((item) => item.count),
                      1,
                    );
                    return (
                      <li key={bucket.bucket} className="flex flex-col gap-1">
                        <div className="flex items-center justify-between text-sm">
                          <span>{bucket.bucket}</span>
                          <span className="font-mono">{bucket.count}</span>
                        </div>
                        <div className="h-2 w-full overflow-hidden rounded-pill bg-ink-700">
                          <div
                            className="h-full bg-accent"
                            style={{ width: `${Math.round((bucket.count / max) * 100)}%` }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-muted">{ru.common.emptyDescription}</p>
              )}
            </Card>

            <Card title={ru.reports.revenueByProgram}>
              {report.revenueByProgram && report.revenueByProgram.length > 0 ? (
                <ul className="flex flex-col gap-2">
                  {report.revenueByProgram.map((item) => (
                    <li key={item.excursionId} className="rounded-card border border-line p-3">
                      <KeyValue label={item.title} value={formatMoney(item.revenue)} />
                      <KeyValue label={ru.reports.visits} value={item.visits} mono />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted">{ru.reports.noProgramRevenue}</p>
              )}
            </Card>

            <Card title={ru.reports.slotsLoad}>
              {slotsLoad.length > 0 ? (
                <ul className="flex flex-col gap-2">
                  {slotsLoad.map((slot) => (
                    <li key={slot.slotId} className="rounded-card border border-line p-3">
                      <KeyValue label={formatDateTimeLocal(slot.startsAt)} value={`${slot.reserved} / ${slot.capacity}`} mono />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted">{ru.common.emptyDescription}</p>
              )}
            </Card>

            <Card title={ru.reports.attendance}>
              {report.attendanceByPeriod && report.attendanceByPeriod.length > 0 ? (
                <ul className="flex flex-col gap-2">
                  {report.attendanceByPeriod.map((item) => (
                    <li key={item.period} className="rounded-card border border-line p-3">
                      <KeyValue label={item.period} value={item.visits} mono />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted">{ru.common.emptyDescription}</p>
              )}
            </Card>
          </>
        ) : null}
      </AsyncState>
    </AppShell>
  );
}
